import { FC, ReactNode } from 'react';
import type { MediaId } from 'models/Media';
import { useMediaUrl } from 'hooks/useMediaUrl';
import Avatar, { AvatarProps } from './Avatar';

export interface MediaAvatarProps extends Omit<AvatarProps, 'src'> {
  /** A stored profile image, e.g. `user.profileImage?.id`. */
  mediaId?: MediaId;
  /**
   * Shown instead of the initials while the photo loads, e.g. a skeleton, so
   * the user's own avatar doesn't jump from initials to photo on every load.
   */
  placeholder?: ReactNode;
}

// An Avatar for a stored profile image. Resolves the signed CDN URL and
// shows the initials while it loads or when the file can't be fetched.
// Avatar itself stays free of data concerns, so it can live anywhere.
const MediaAvatar: FC<MediaAvatarProps> = ({
  mediaId,
  placeholder,
  ...props
}) => {
  // The original, not the small square copy: the server makes that copy a
  // few seconds after an upload, so a new photo would show as broken.
  const { url, status, onError } = useMediaUrl(mediaId);
  const loading = !!mediaId && status === 'loading';

  return (
    <Avatar
      {...props}
      icon={loading && placeholder ? placeholder : props.icon}
      src={status === 'ready' ? (url ?? undefined) : undefined}
      // Signed CDN URLs aren't on the image host allow-list.
      unoptimized
      onImageError={onError}
    />
  );
};

export default MediaAvatar;
